import { WISH_PRIORITIES } from "@listou/shared/constants";
import type { PublicList, PublicWish } from "@listou/shared/types/api";
import { createContext, useContext, useState, type CSSProperties, type ReactNode } from "react";

import { countdownLabel, formatDay, formatPrice } from "../../lib/format";
import { stickerFor } from "../../lib/theme";

export type MyReservation = { id: string; wishId: string };

/** What every template renders from. Built once in ListPage. */
export type TemplateProps = {
  list: PublicList;
  wishes: PublicWish[];
  stickers: string[];
  display: string;
  mine: MyReservation[];
  onReserve?: (wish: PublicWish) => void;
  onCancel?: (reservation: MyReservation) => void;
  actions?: ReactNode;
  /** The sort control, drawn by each template right above its wishes. */
  toolbar?: ReactNode;
  preview: boolean;
  /** The list title's element: a page embedding the list already has its own h1. */
  Headline: "h1" | "p";
};

const PRIORITY_RANK = Object.fromEntries(WISH_PRIORITIES.map((value, index) => [value, index]));

export const WISH_SORTS = [
  { id: "list", label: "Coups de cœur d'abord" },
  { id: "available", label: "À offrir d'abord" },
  { id: "price-asc", label: "Prix croissant" },
  { id: "price-desc", label: "Prix décroissant" },
] as const;

export type WishSort = (typeof WISH_SORTS)[number]["id"];

const isTaken = (wish: PublicWish) => wish.reservedQuantity >= wish.quantity;

/**
 * Coups de cœur first and the owner's manual order within each priority, unless the guest
 * picked another order. Every other sort falls back to that one, so ties stay stable, and
 * a wish without a price goes last whichever way prices run.
 */
export const sortWishes = (wishes: PublicWish[], sort: WishSort = "list") => {
  const byList = wishes.toSorted(
    (a, b) => (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9),
  );
  if (sort === "list") return byList;
  if (sort === "available")
    return byList.toSorted((a, b) => Number(isTaken(a)) - Number(isTaken(b)));
  const direction = sort === "price-asc" ? 1 : -1;
  return byList.toSorted((a, b) => {
    if (a.priceCents === null || b.priceCents === null) {
      return Number(a.priceCents === null) - Number(b.priceCents === null);
    }
    return (a.priceCents - b.priceCents) * direction;
  });
};

/** Guests reorder the page by price or availability. Hidden when there is nothing to sort. */
export function SortBar({
  value,
  onChange,
  className = "",
}: {
  value: WishSort;
  onChange: (sort: WishSort) => void;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Trier les envies"
      className={`flex flex-wrap items-center gap-2 ${className}`}
    >
      <span className="text-sm font-bold text-muted">Trier</span>
      {WISH_SORTS.map((sort) => (
        <button
          key={sort.id}
          type="button"
          role="radio"
          aria-checked={value === sort.id}
          onClick={() => onChange(sort.id)}
          className={`min-h-9 rounded-full border-[1.5px] px-3 text-sm font-bold transition ${
            value === sort.id
              ? "border-ink bg-ink text-surface"
              : "border-line bg-surface text-ink hover:border-ink/40"
          }`}
        >
          {sort.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A pack sticker. Packs are generated in batches, so a file may not exist yet on a fresh
 * deploy: a broken image is hidden rather than shown as a torn icon.
 */
export function Sticker({
  src,
  className = "",
  style,
}: {
  src: string | null | undefined;
  className?: string;
  style?: CSSProperties;
}) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return null;
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      data-sticker
      draggable={false}
      loading="lazy"
      onError={() => setBroken(true)}
      className={`pointer-events-none select-none ${className}`}
      style={style}
    />
  );
}

/**
 * Which sticker stands in for each wish without a photo, decided once for the whole page
 * so neighbouring cards never show the same one (a per-wish hash collides constantly with
 * nine stickers to pick from).
 */
export const PlaceholderStickers = createContext<Map<string, string | null>>(new Map());

export const assignPlaceholders = (
  wishes: PublicWish[],
  stickers: string[],
): Map<string, string | null> => {
  const map = new Map<string, string | null>();
  if (stickers.length === 0) return map;
  // Start after the first sticker: that one usually leads the hero.
  let next = 1;
  for (const wish of wishes) {
    if (wish.imageUrl) continue;
    map.set(wish.id, stickers[next % stickers.length] ?? null);
    next += 1;
  }
  return map;
};

/** The wish photo, or one of the pack's stickers when there is none. */
export function WishImage({
  wish,
  stickers,
  className = "",
  fit = "contain",
}: {
  wish: PublicWish;
  stickers: string[];
  className?: string;
  /** `full`: edge to edge like `cover` on a square photo, but never crops a product. */
  fit?: "contain" | "cover" | "full";
}) {
  const [broken, setBroken] = useState(false);
  const placeholders = useContext(PlaceholderStickers);
  const taken = isTaken(wish);
  const badges = (
    <>
      {wish.priority === "love" && <HeartSticker />}
      {taken && <TakenCheck />}
    </>
  );
  if (wish.imageUrl && !broken) {
    return (
      <div className={`relative grid place-items-center overflow-hidden bg-white ${className}`}>
        <img
          src={wish.imageUrl}
          alt=""
          loading="lazy"
          onError={() => setBroken(true)}
          className={`size-full ${
            fit === "cover"
              ? "object-cover"
              : fit === "full"
                ? "object-contain"
                : "object-contain p-[7%]"
          } ${taken ? "grayscale-[55%]" : ""}`}
        />
        {badges}
      </div>
    );
  }
  return (
    <div className={`relative grid place-items-center overflow-hidden ${className}`}>
      <WishPlaceholder
        title={wish.title}
        // The page hands each wish a distinct sticker when it can; the hash is the fallback.
        sticker={
          placeholders.has(wish.id)
            ? (placeholders.get(wish.id) ?? null)
            : stickerFor(stickers, wish.id)
        }
      />
      {badges}
    </div>
  );
}

/**
 * A red heart stuck on a coup de cœur's photo: seen before any text is read, and the same
 * red whatever the palette, because it means the same thing on every list.
 */
function HeartSticker() {
  return (
    <svg
      viewBox="0 0 48 44"
      role="img"
      aria-label="Coup de cœur"
      className="absolute top-[5%] right-[5%] w-[30%] max-w-24 min-w-9 rotate-12 drop-shadow-[0_2px_3px_rgb(0_0_0/0.25)]"
    >
      <path
        d="M24 41S3 28.6 3 14.6C3 7.8 8.2 3 14.2 3c4.3 0 7.8 2.4 9.8 5.9C26 5.4 29.5 3 33.8 3 39.8 3 45 7.8 45 14.6 45 28.6 24 41 24 41Z"
        fill="#e11d48"
        stroke="#fff"
        strokeWidth="3.5"
        strokeLinejoin="round"
      />
      <path
        d="M12 12.5c1-2.4 3-3.6 5.2-3.6"
        stroke="#fff"
        strokeOpacity="0.7"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Given already: a green check over the photo, read at a glance while scrolling. */
function TakenCheck() {
  return (
    <span aria-hidden className="absolute inset-0 grid place-items-center bg-surface/35">
      <svg
        viewBox="0 0 48 48"
        className="w-[34%] max-w-24 min-w-10 drop-shadow-[0_2px_4px_rgb(0_0_0/0.25)]"
      >
        <circle cx="24" cy="24" r="21" fill="#16a34a" stroke="#fff" strokeWidth="3.5" />
        <path
          d="m14.5 24.5 6.5 6.5 12.5-13"
          stroke="#fff"
          strokeWidth="4.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </span>
  );
}

/**
 * What stands in for a missing photo: a sticker from the pack, or, on a list without
 * stickers, the wish's initial set large on a tint of the palette. Never an empty box.
 */
export function WishPlaceholder({ title, sticker }: { title: string; sticker: string | null }) {
  return (
    <div
      // A container of its own, so the initial scales with the slot: a 64px thumbnail in the
      // editor and a full card on the public page both get a letter that fits.
      className="@container grid size-full place-items-center"
      style={{
        background: "color-mix(in srgb, var(--color-accent) 12%, var(--color-surface))",
      }}
    >
      {sticker ? (
        <Sticker src={sticker} className="size-[62%] object-contain drop-shadow-sm" />
      ) : (
        <span
          aria-hidden
          className="font-soft grid aspect-square w-[46%] place-items-center rounded-full text-[26cqi] leading-none font-medium text-accent-text"
          style={{
            background: "color-mix(in srgb, var(--color-accent) 16%, var(--color-surface))",
          }}
        >
          {title.trim().charAt(0).toUpperCase() || "?"}
        </span>
      )}
    </div>
  );
}

export const priceLabel = (wish: PublicWish) => {
  const price = formatPrice(wish.priceCents, wish.currency);
  return price ? `Environ ${price}` : null;
};

/**
 * Price and quantity on one line. A wish without a known price shows nothing rather than
 * "Prix libre": the price is usually only unreadable, not free.
 */
export function PriceLine({ wish, className = "" }: { wish: PublicWish; className?: string }) {
  const price = priceLabel(wish);
  const many = wish.quantity > 1 ? `${wish.quantity} souhaités` : null;
  if (!price && !many) return null;
  return <p className={className}>{[price, many].filter(Boolean).join(" · ")}</p>;
}

/** "Idée": the owner would be happy with it, without it being a must. */
export function IdeaTag({ wish, className = "" }: { wish: PublicWish; className?: string }) {
  if (wish.priority !== "idea") return null;
  return (
    <p
      className={`w-fit rounded-full bg-ink/[0.07] px-2.5 py-0.5 text-sm font-bold text-muted ${className}`}
    >
      Idée
    </p>
  );
}

/** "Déjà offert" / "2 sur 3 réservés". Nothing when untouched. */
export function WishStatus({ wish, className = "" }: { wish: PublicWish; className?: string }) {
  if (wish.reservedQuantity === 0) return null;
  const full = isTaken(wish);
  return (
    <p
      // Taken, it replaces "Je l'offre": same height, centred, so the row stays aligned.
      className={`rounded-xl px-3 py-2 font-bold ${
        full
          ? // Ink, not a dark green: the tint sits on the page's surface, which may be dark.
            "flex min-h-12 items-center justify-center gap-2 bg-[#16a34a]/15 text-center text-ink"
          : "bg-butter text-[#3a2d00]"
      } ${className}`}
    >
      {full && (
        <svg viewBox="0 0 24 24" aria-hidden className="size-5 shrink-0">
          <circle cx="12" cy="12" r="11" fill="#16a34a" />
          <path
            d="m7 12.5 3.2 3.2L17 9"
            stroke="#fff"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      )}
      {full ? "Déjà offert" : `${wish.reservedQuantity} sur ${wish.quantity} réservés`}
    </p>
  );
}

export function WishActions({
  wish,
  mine,
  onReserve,
  onCancel,
  preview,
  variant = "pill",
}: {
  wish: PublicWish;
  mine: MyReservation[];
  onReserve?: (wish: PublicWish) => void;
  onCancel?: (reservation: MyReservation) => void;
  preview: boolean;
  variant?: "pill" | "pop" | "square";
}) {
  const remaining = Math.max(0, wish.quantity - wish.reservedQuantity);
  const primary =
    variant === "pop"
      ? "pop-btn w-full"
      : variant === "square"
        ? "btn w-full rounded-lg"
        : "btn w-full min-h-13 text-lg";
  const secondary = `btn-secondary w-full min-w-0 px-4 whitespace-nowrap ${
    variant === "square" ? "rounded-lg" : ""
  }`;
  const mineHere = mine.filter((reservation) => reservation.wishId === wish.id);
  // An empty box would still take a grid gap and push the status above its neighbours.
  if (mineHere.length === 0 && remaining === 0 && !wish.goUrl) return null;

  return (
    <div className="grid gap-2">
      {mineHere.length > 0 ? (
        <div className="grid gap-2 rounded-2xl bg-fir p-3 text-white">
          <p className="font-bold">C'est vous qui l'offrez. Merci !</p>
          {onCancel && !preview && (
            <button
              type="button"
              className="min-h-10 rounded-full bg-white/15 px-4 font-bold hover:bg-white/25"
              onClick={() => {
                const first = mineHere[0];
                if (first) onCancel(first);
              }}
            >
              Annuler ma réservation
            </button>
          )}
        </div>
      ) : (
        remaining > 0 && (
          <button
            type="button"
            className={primary}
            onClick={() => onReserve?.(wish)}
            // The editor preview shows the real button, but it must not do anything there.
            tabIndex={preview ? -1 : undefined}
            aria-disabled={preview || undefined}
          >
            Je l'offre
          </button>
        )
      )}
      {wish.goUrl && (
        <a
          href={preview ? undefined : wish.goUrl}
          target="_blank"
          rel="sponsored nofollow noopener"
          // Given already: the shop link stays for the curious, but no longer calls out.
          className={
            remaining === 0 && mineHere.length === 0
              ? "justify-self-center px-2 py-1 text-sm text-muted underline underline-offset-2 hover:text-ink"
              : secondary
          }
          title={wish.merchant ? `Voir sur ${wish.merchant}` : undefined}
          tabIndex={preview ? -1 : undefined}
        >
          {/* One line whatever the domain: a pill wrapped over two lines reads as broken. */}
          <span className="truncate">
            Voir {wish.merchant ? `sur ${wish.merchant}` : "le produit"}
          </span>
        </a>
      )}
    </div>
  );
}

/** Date and countdown, when the list has an event day. */
export function EventDate({ list, className = "" }: { list: PublicList; className?: string }) {
  const day = formatDay(list.eventDate);
  if (!day) return null;
  const countdown = countdownLabel(list.eventDate);
  return (
    <p className={`flex flex-wrap items-baseline gap-x-3 ${className}`}>
      <span>{day}</span>
      {countdown && <span className="font-bold text-accent-text">{countdown}</span>}
    </p>
  );
}

export const byLine = (list: PublicList) =>
  list.ownerNames.length > 0 ? `Une liste de ${list.ownerNames.join(" et ")}` : null;

export function EmptyWishes({ stickers }: { stickers: string[] }) {
  return (
    <div className="grid justify-items-center gap-3 rounded-3xl bg-surface p-10 text-center">
      <Sticker src={stickers[0]} className="size-28 object-contain" />
      <p className="max-w-[34ch] text-lg text-muted">
        La liste se remplit encore. Revenez dans quelques jours pour trouver le cadeau idéal.
      </p>
    </div>
  );
}

/** "Participer librement": the owners' money pot, for guests who would rather contribute. */
export function FundCard({ list, preview }: { list: PublicList; preview: boolean }) {
  if (!list.fundUrl) return null;
  return (
    <section className="mx-auto max-w-3xl px-5 pb-12">
      <div className="grid gap-3 rounded-3xl bg-surface p-6 text-center shadow-sm ring-1 ring-line @2xl:p-8">
        <h2 className="font-display text-2xl font-bold">Participer librement</h2>
        <p className="mx-auto max-w-[46ch] text-lg text-ink/85">
          Pas d'idée dans la liste ? Vous pouvez aussi mettre ce que vous voulez dans la cagnotte.
        </p>
        <a
          href={preview ? undefined : list.fundUrl}
          target="_blank"
          rel="noopener"
          tabIndex={preview ? -1 : undefined}
          className="btn-secondary justify-self-center"
        >
          Ouvrir la cagnotte
        </a>
      </div>
    </section>
  );
}

export function AffiliateNote({ list }: { list: PublicList }) {
  if (!list.hasAffiliateLinks) return null;
  return (
    <p className="text-sm text-muted">
      Certains liens sont affiliés : Listou peut toucher une commission, sans surcoût pour vous.
    </p>
  );
}
